import { writable, get } from 'svelte/store';
import type { Environment, ChatMessage, AgentStatus, ToolCall, SSEEvent } from '../types/debugger';
import { streamAgentChat } from '../services/debugger-agent-client';
import { searchState } from './debugger-timeline';

export const agentStatus = writable<AgentStatus>('idle');
export const chatMessages = writable<ChatMessage[]>([]);
export const sessionId = writable<string>(crypto.randomUUID());

function handleEvent(event: SSEEvent) {
  switch (event.event) {
    case 'reasoning':
      agentStatus.set('thinking');
      break;
    case 'tool_call':
      agentStatus.set('tool_calling');
      chatMessages.update(msgs => {
        const last = msgs[msgs.length - 1];
        if (last?.role === 'assistant') {
          const tc: ToolCall = { id: event.payload.id, name: event.payload.name, status: 'running', input: event.payload.input };
          last.toolCalls = [...(last.toolCalls || []), tc];
          return [...msgs.slice(0, -1), { ...last }];
        }
        return msgs;
      });
      break;
    case 'tool_response':
      chatMessages.update(msgs => {
        const last = msgs[msgs.length - 1];
        if (last?.role === 'assistant' && last.toolCalls) {
          const tc = last.toolCalls.find(t => t.id === event.payload.id);
          if (tc) {
            tc.status = 'complete';
            tc.output = event.payload.output;
            tc.duration = event.payload.duration;
          }
          return [...msgs.slice(0, -1), { ...last, toolCalls: [...last.toolCalls] }];
        }
        return msgs;
      });
      break;
    case 'response':
      agentStatus.set('streaming');
      chatMessages.update(msgs => {
        const last = msgs[msgs.length - 1];
        if (last?.role === 'assistant') {
          return [...msgs.slice(0, -1), { ...last, content: last.content + event.payload.text, isStreaming: true }];
        }
        return msgs;
      });
      break;
    case 'final_response':
      agentStatus.set('idle');
      chatMessages.update(msgs => {
        const last = msgs[msgs.length - 1];
        if (last?.role === 'assistant') {
          return [...msgs.slice(0, -1), { ...last, content: event.payload.text, isStreaming: false }];
        }
        return msgs;
      });
      break;
    case 'error':
      agentStatus.set('error');
      break;
  }
}

export async function sendMessage(message: string) {
  const env = get(searchState).environment;
  const sid = get(sessionId);

  // Add user message
  chatMessages.update(msgs => [...msgs, {
    id: crypto.randomUUID(), role: 'user', content: message,
    timestamp: new Date().toISOString(),
  }]);

  // Add empty assistant message for streaming
  chatMessages.update(msgs => [...msgs, {
    id: crypto.randomUUID(), role: 'assistant', content: '',
    timestamp: new Date().toISOString(), isStreaming: true, toolCalls: [],
  }]);

  agentStatus.set('thinking');
  try {
    await streamAgentChat(env, message, sid, handleEvent);
  } catch (e: any) {
    agentStatus.set('error');
    chatMessages.update(msgs => {
      const last = msgs[msgs.length - 1];
      if (last?.role === 'assistant') {
        return [...msgs.slice(0, -1), { ...last, content: `Error: ${e.message}`, isStreaming: false }];
      }
      return msgs;
    });
  }
}

export function clearChat() {
  chatMessages.set([]);
  sessionId.set(crypto.randomUUID());
  agentStatus.set('idle');
}
